import React, { useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Tooltip from '../components/Tooltip';

interface Persona {
  title: string;
  description: string;
  keyPoints: (string | React.ReactNode)[];
}

const personas: Persona[] = [
  {
    title: "Conservation Practitioners, Governments & Marine Protected Areas",
    description: "Evidence-based conservation strategies and ecosystem monitoring for effective marine protection",
    keyPoints: [
      "Robust biodiversity data for MPA design and effectiveness evaluation",
      "Food web modeling and ecosystem connectivity mapping",
      "Real-time monitoring of invasive, endangered, and keystone species",
      "Adaptive fisheries management through ecosystem-level analysis",
      "Community-centered partnerships for equitable conservation"
    ]
  },
  {
    title: "Low & Middle Income Countries",
    description: "Sustainable blue economic development through biotechnology and nature-based solutions",
    keyPoints: [
      (<>
        Turnkey <Tooltip definition="Marine Genetic Resources (MGRs) are the genetic material contained in marine organisms, including microbes, plants, and animals, that have actual or potential value. MGRs may include genes coding for enzymes, peptides, or other biomolecules with applications in biotechnology, medicine, and industry." term="Marine Genetic Resources">Marine Genetic Resources</Tooltip> discovery with sovereign <Tooltip definition="Benefit-sharing refers to adhering to international and national frameworks, foremost the Nagoya Protocol, that regulate access to genetic resources and ensure that benefits arising from their use (scientific, financial, or technological) are shared fairly and equitably with the country or community of origin." term="Benefit-sharing">benefit-sharing</Tooltip>
      </>),
      "Access to multilateral funding through enhanced biotechnological capacities",
      "Sustainable blue economy development, including eco-tourism and marine stewardship",
      "Transparent, equitable approaches building long-term trust",
      "Scientific expertise and technological innovation transfer"
    ]
  },
  {
    title: "Nature-Based Finance & Investment Community",
    description: "Data-driven ecological risk assessment and investment opportunity identification",
    keyPoints: [
      "Ecological risk evaluation and investment opportunity insights",
      "High-impact intervention identification through vulnerability analysis",
      "Due diligence support with holistic ecosystem network context",
      "Robust metrics for measuring conservation outcomes",
      "De-risking nature-based finance instruments"
    ]
  },
  {
    title: "Corporate Sector",
    description: "Science-based biodiversity intelligence for nature-positive corporate strategies",
    keyPoints: [
      "Credible, science-based biodiversity intelligence for nature-positive strategies",
      "Regulatory compliance with auditable data for Corporate Disclosure Frameworks",
      "Nature-dependent asset valuation and environmental forecasting",
      "Catastrophic event modeling and ecosystem-linked risk assessment",
      "Measurable conservation impact demonstration"
    ]
  },
  {
    title: "Foundations & Philanthropic Institutions",
    description: "Transparent impact monitoring and local capacity building in biotechnology",
    keyPoints: [
      "Development of financial mechanisms to accelerate effective marine protection",
      "Impact-driven monitoring demonstrating conservation outcomes from finance flows",
      "Local capacity building through training and technology transfer",
      "Infrastructure development in regions of interest",
      "Transparent reporting on measurable conservation results"
    ]
  }
];

const WhoWeServePage: React.FC = () => {
  useEffect(() => {
    // Always scroll to top when this page loads
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="d-flex flex-column min-vh-100" style={{
      background: 'linear-gradient(135deg, #0A0E14 0%, #14191F 30%, #1E252D 70%, #2A3541 100%)',
      backgroundAttachment: 'fixed'
    }}>
      <Header isMainPage={false} />

      <main className="flex-grow-1">
        <section className="who-we-serve py-5" style={{
          marginTop: '160px',
          position: 'relative'
        }}>
          <style>
            {`.who-we-serve::before { display: none; }`}
          </style>
          <div className="container">
            <div className="row justify-content-center mb-5">
              <div className="col-lg-8 text-center">
                <h1 className="display-4 mb-4" style={{
                  color: '#EFEFE3',
                  fontWeight: 700,
                  fontFamily: "'Montserrat', sans-serif"
                }}>
                  Who We Serve
                </h1>
                <p className="lead mb-5" style={{
                  fontSize: '1.2rem',
                  color: '#D5D6CC',
                  lineHeight: '1.6'
                }}>
                  Ocean BioMetrics partners with diverse stakeholders to advance marine conservation
                  and sustainable blue economy development through cutting-edge genomic intelligence.
                </p>
              </div>
            </div>

            <div className="row g-4">
              {personas.map((persona, index) => (
                <div key={index} className="col-lg-6 col-xl-4">
                  <div className="persona-card h-100" style={{
                    background: 'rgba(239, 239, 227, 0.04)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(239, 239, 227, 0.08)',
                    borderRadius: '16px',
                    padding: '2rem',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                    transition: 'all 0.4s ease',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-8px)';
                      e.currentTarget.style.boxShadow = '0 16px 40px rgba(8, 136, 208, 0.15)';
                      e.currentTarget.style.borderColor = 'rgba(8, 136, 208, 0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 8px 32px rgba(0,0,0,0.2)';
                      e.currentTarget.style.borderColor = 'rgba(239, 239, 227, 0.08)';
                    }}>

                    <div className="text-center mb-3">
                      <h3 style={{
                        color: '#EFEFE3',
                        fontSize: '1.4rem',
                        fontWeight: 700,
                        fontFamily: "'Montserrat', sans-serif",
                        lineHeight: '1.3',
                        marginBottom: '1rem'
                      }}>
                        {persona.title}
                      </h3>
                      <p style={{
                        color: '#18C3BA',
                        fontSize: '1rem',
                        fontWeight: 500,
                        lineHeight: '1.5',
                        marginBottom: '1.5rem'
                      }}>
                        {persona.description}
                      </p>
                    </div>

                    <div className="flex-grow-1">
                      <ul className="list-unstyled" style={{ marginBottom: 0 }}>
                        {persona.keyPoints.map((point, pointIndex) => (
                          <li key={pointIndex} className="mb-3" style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '0.75rem'
                          }}>
                            <span style={{
                              color: '#0888D0',
                              fontSize: '0.8rem',
                              marginTop: '0.2rem',
                              flexShrink: 0
                            }}>
                              ▶
                            </span>
                            <span style={{
                              color: '#D5D6CC',
                              fontSize: '0.9rem',
                              lineHeight: '1.5'
                            }}>
                              {point}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center mt-5 pt-4">
              <p className="mb-3" style={{ fontSize: '1.1rem', color: '#D5D6CC' }}>
                Ready to partner with Ocean BioMetrics for your marine conservation and blue economy initiatives?
              </p>
              <a href="/#contact" className="btn" style={{
                background: 'linear-gradient(90deg, #0888D0, #18C3BA)',
                border: 'none',
                color: '#EFEFE3',
                padding: '0.75rem 2.5rem',
                borderRadius: '25px',
                fontWeight: 600,
                fontFamily: "'Inter', 'Roboto', sans-serif",
                transition: 'all 0.3s ease',
                textDecoration: 'none',
                display: 'inline-block',
                fontSize: '1.1rem'
              }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                  e.currentTarget.style.boxShadow = '0 0 25px rgba(8, 136, 208, 0.4)';
                  e.currentTarget.style.background = 'linear-gradient(90deg, #18C3BA, #0888D0)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.background = 'linear-gradient(90deg, #0888D0, #18C3BA)';
                }}
              >
                Get Started →
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default WhoWeServePage;